// src/pages/settings/Settings.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  FormControlLabel, 
  Switch, 
  Divider, 
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  InputAdornment,
  Tab,
  Tabs,
  ThemeProvider,
  createTheme,
  Container,
  Fade,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Badge
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  SmartToy as SmartToyIcon,
  Store as StoreIcon,
  Settings as SettingsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Restaurant as RestaurantIcon,
  SportsEsports as SportsIcon,
  Link as LinkIcon,
  Euro as EuroIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  Help as HelpIcon,
  ContactPhone as ContactPhoneIcon,
  CreditCard as CreditCardIcon,
  CalendarMonth as CalendarMonthIcon,
  WarningAmber as WarningAmberIcon,
  PhoneAndroid as PhoneAndroidIcon
} from '@mui/icons-material';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

// Tema personalizado con paleta verde pastel
const pastelGreenTheme = createTheme({
  palette: {
    primary: {
      main: '#81C784', // Verde pastel principal
      light: '#A5D6A7',
      dark: '#66BB6A',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#C8E6C9', // Verde pastel más claro
      light: '#E8F5E8',
      dark: '#A5D6A7',
      contrastText: '#2E7D32',
    },
    background: {
      default: '#F1F8E9',
      paper: '#FFFFFF',
    },
    success: {
      main: '#4CAF50',
      light: '#81C784',
    },
    info: {
      main: '#26A69A',
      light: '#80CBC4',
    },
    warning: {
      main: '#FFA726',
      light: '#FFB74D',
    },
    error: {
      main: '#EF5350',
      light: '#E57373',
    },
    text: {
      primary: '#1B5E20',
      secondary: '#388E3C',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(129, 199, 132, 0.15)',
          borderRadius: '12px',
          border: '1px solid #E8F5E8',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(129, 199, 132, 0.15)',
          borderRadius: '12px',
          border: '1px solid #E8F5E8',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(129, 199, 132, 0.25)',
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(129, 199, 132, 0.15)',
          borderRadius: '12px !important',
          border: '1px solid #E8F5E8',
          '&:before': {
            display: 'none',
          },
          marginBottom: '16px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          boxShadow: '0 2px 8px rgba(129, 199, 132, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(129, 199, 132, 0.4)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#81C784',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#66BB6A',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px 8px 0 0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#E8F5E8',
          color: '#2E7D32',
          fontWeight: 500,
        },
      },
    },
  },
});

// Hooks personalizados
const useSettings = () => {
  const [settings, setSettings] = useState({
    appName: 'AMPA del Bosc',
    shopEnabled: true,
    notificationsEnabled: true,
    servicesEnabled: true,
    contactEmail: '',
    contactPhone: '',
    schoolAddress: '',
    welcomeMessage: ''
  });

  const [shopSettings, setShopSettings] = useState({
    shippingCost: '0',
    minOrderForFreeShipping: '0',
    paymentMethods: ['transferencia', 'efectivo'],
    newPaymentMethod: ''
  });

  const [chatbotSettings, setChatbotSettings] = useState({
    ampa: {
      nombre: 'AMPA del Bosc',
      direccion: '',
      telefono_principal: '',
      email_principal: '',
      horario_atencion: '',
      web_oficial: '',
      descripcion: ''
    },
    comedor: {
      activo: true,
      empresa_proveedora: 'EcoTresmes',
      telefono: '',
      email: '',
      web: '',
      precios: {
        nino_completo: '6.7',
        nino_medio: '4.5',
        adulto: '8.0'
      },
      horarios: 'de lunes miércoles y viernes de 16:00 a 18:00',
      info_adicional: 'Servicios contratados con empresas externas Servicio de comedor: Gestionado por EcoTresmes, empresa especializada en alimentación escolar ecológica y de proximidad.',
      alergenos: '',
      menu_semanal_url: ''
    },
    acogida: {
      activo: true,
      empresa_proveedora: 'Eduactivities',
      telefono: '',
      email: '',
      web: '',
      precios: {
        matinal: '5.0',
        tardia: '6.0',
        completa: '10.0'
      },
      horarios: {
        matinal: '07:30 - 09:00',
        tardia: '17:00 - 18:30'
      },
      info_adicional: 'Servicio de acogida matinal y tardía: A cargo de Eduactivities.',
      actividades: []
    },
    extraescolares: {
      activo: true,
      info_general: 'Actividades extraescolares deportivas, artísticas y formativas.',
      proveedores: [
        {
          nombre: 'Eduactivities',
          servicios: ['teatro', 'inglés', 'robótica'],
          telefono: '',
          email: '',
          web: '',
          descripcion: ''
        }
      ],
      nuevo_proveedor: {
        nombre: '',
        servicios: '',
        telefono: '',
        email: '',
        web: '',
        descripcion: ''
      }
    },
    contactos: {
      direccion_escuela: '',
      secretaria_escuela: {
        telefono: '',
        email: '',
        horario: ''
      },
      conserjeria: {
        telefono: '',
        horario: ''
      },
      emergencias: {
        telefono_urgencias: '',
        contacto_fuera_horario: ''
      }
    },
    faq: {
      horarios_escuela: '',
      proceso_inscripcion: '',
      becas_ayudas: '',
      actividades_ampa: '',
      como_participar: '',
      reuniones_fechas: ''
    }
  });

  return {
    settings,
    setSettings,
    shopSettings,
    setShopSettings,
    chatbotSettings,
    setChatbotSettings
  };
};

// Componente TabPanel
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Fade in={value === index} timeout={300}>
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      </Fade>
    )}
  </div>
);

// Componente de campo de precio
const PriceField = ({ label, value, onChange, ...props }) => (
  <TextField
    fullWidth
    label={label}
    value={value}
    onChange={onChange}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <EuroIcon color="primary" />
        </InputAdornment>
      ),
    }}
    {...props}
  />
);

// Componente de sección de configuración
const SettingsSection = ({ title, icon, children, defaultExpanded = false }) => (
  <Accordion defaultExpanded={defaultExpanded}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon}
        <Typography variant="h6" color="primary">
          {title}
        </Typography>
      </Stack>
    </AccordionSummary>
    <AccordionDetails>
      {children}
    </AccordionDetails>
  </Accordion>
);

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function getAcademicYear(month, day) {
  const now = new Date();
  const start = new Date(now.getFullYear(), month - 1, day);
  return now >= start
    ? `${now.getFullYear()}-${now.getFullYear() + 1}`
    : `${now.getFullYear() - 1}-${now.getFullYear()}`;
}

const APP_CONFIG_DEFAULTS = {
  isPaymentActive: false,
  'ampa-cuote': 0,
  academicYearStartMonth: 9,
  academicYearStartDay: 1,
  renewalDays: 30,
  stripeTestMode: true,
  stripeWebhookUrl: '',
  stripeWebhookUrlTest: '',
  appStoreUrl: '',
  playStoreUrl: '',
  updatedAt: null
};

// Componente principal
const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const [appConfig, setAppConfig] = useState(APP_CONFIG_DEFAULTS);
  const [savedAppConfig, setSavedAppConfig] = useState(null);
  const [showTestModeConfirm, setShowTestModeConfirm] = useState(false);
  
  const {
    settings,
    setSettings,
    shopSettings,
    setShopSettings,
    chatbotSettings,
    setChatbotSettings
  } = useSettings();

  // Cargar configuraciones al iniciar
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [generalDoc, shopDoc, chatbotDoc, configDoc] = await Promise.all([
          getDoc(doc(db, 'settings', 'general')),
          getDoc(doc(db, 'settings', 'shop')),
          getDoc(doc(db, 'settings', 'chatbot')),
          getDoc(doc(db, 'settings', 'config'))
        ]);

        if (generalDoc.exists()) {
          setSettings(prev => ({ ...prev, ...generalDoc.data() }));
        }

        if (shopDoc.exists()) {
          const data = shopDoc.data();
          setShopSettings(prev => ({
            ...prev,
            shippingCost: data.shippingCost?.toString() || '0',
            minOrderForFreeShipping: data.minOrderForFreeShipping?.toString() || '0',
            paymentMethods: data.paymentMethods || ['transferencia', 'efectivo']
          }));
        }

        if (chatbotDoc.exists()) {
          setChatbotSettings(prev => ({ ...prev, ...chatbotDoc.data() }));
        }

        if (configDoc.exists()) {
          const cfg = { ...APP_CONFIG_DEFAULTS, ...configDoc.data() };
          setAppConfig(cfg);
          setSavedAppConfig(cfg);
        } else {
          setSavedAppConfig(APP_CONFIG_DEFAULTS);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        setError('Error al cargar la configuración');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);

  // Manejadores de eventos
  const handleSettingsChange = (e) => {
    const { name, value, checked, type } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleShopSettingsChange = (e) => {
    const { name, value } = e.target;
    
    if (['shippingCost', 'minOrderForFreeShipping'].includes(name)) {
      const regex = /^$|^[0-9]+(\.[0-9]{0,2})?$/;
      if (!regex.test(value)) return;
    }
    
    setShopSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleChatbotSettingsChange = (section, field, value, subfield = null) => {
    setChatbotSettings(prev => {
      const newSettings = JSON.parse(JSON.stringify(prev));
      if (subfield) {
        newSettings[section][field][subfield] = value;
      } else {
        newSettings[section][field] = value;
      }
      return newSettings;
    });
  };

  const handleAddPaymentMethod = () => {
    if (!shopSettings.newPaymentMethod.trim()) return;
    
    if (!shopSettings.paymentMethods.includes(shopSettings.newPaymentMethod)) {
      setShopSettings(prev => ({
        ...prev,
        paymentMethods: [...prev.paymentMethods, prev.newPaymentMethod],
        newPaymentMethod: ''
      }));
    }
  };

  const handleRemovePaymentMethod = (method) => {
    setShopSettings(prev => ({
      ...prev,
      paymentMethods: prev.paymentMethods.filter(m => m !== method)
    }));
  };

  const handleAddProveedor = () => {
    const { nuevo_proveedor } = chatbotSettings.extraescolares;
    
    if (!nuevo_proveedor.nombre.trim()) return;
    
    const serviciosArray = nuevo_proveedor.servicios
      .split(',')
      .map(s => s.trim())
      .filter(s => s);
    
    const nuevoProveedor = { ...nuevo_proveedor, servicios: serviciosArray };
    
    setChatbotSettings(prev => ({
      ...prev,
      extraescolares: {
        ...prev.extraescolares,
        proveedores: [...prev.extraescolares.proveedores, nuevoProveedor],
        nuevo_proveedor: {
          nombre: '',
          servicios: '',
          telefono: '',
          email: '',
          web: '',
          descripcion: ''
        }
      }
    }));
  };

  const handleRemoveProveedor = (index) => {
    setChatbotSettings(prev => ({
      ...prev,
      extraescolares: {
        ...prev.extraescolares,
        proveedores: prev.extraescolares.proveedores.filter((_, i) => i !== index)
      }
    }));
  };

  const handleAppConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAppConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const isAppConfigDirty = savedAppConfig && JSON.stringify({
    ...appConfig, updatedAt: null
  }) !== JSON.stringify({ ...savedAppConfig, updatedAt: null });

  const doSave = async () => {
    setSaving(true);
    setError('');
    try {
      const { updatedAt, ...configToSave } = appConfig;
      await Promise.all([
        setDoc(doc(db, 'settings', 'general'), settings, { merge: true }),
        setDoc(doc(db, 'settings', 'shop'), {
          shippingCost: parseFloat(shopSettings.shippingCost || '0'),
          minOrderForFreeShipping: parseFloat(shopSettings.minOrderForFreeShipping || '0'),
          paymentMethods: shopSettings.paymentMethods
        }, { merge: true }),
        setDoc(doc(db, 'settings', 'chatbot'), chatbotSettings, { merge: true }),
        setDoc(doc(db, 'settings', 'config'), {
          ...configToSave,
          updatedAt: serverTimestamp()
        }, { merge: true })
      ]);
      setSavedAppConfig(appConfig);
      setSuccess(true);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = () => {
    const goingToProduction = savedAppConfig?.stripeTestMode === true && appConfig.stripeTestMode === false;
    if (goingToProduction) {
      setShowTestModeConfirm(true);
    } else {
      doSave();
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={pastelGreenTheme}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          bgcolor: 'background.default'
        }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={pastelGreenTheme}>
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 3
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            gutterBottom 
            color="primary"
            sx={{ 
              fontWeight: 700,
              textAlign: 'center',
              mb: 4
            }}
          >
            Configuración del Sistema
          </Typography>

          {/* Pestañas de navegación */}
          <Paper sx={{ mb: 4, overflow: 'hidden' }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab
                icon={<SettingsIcon />}
                label="General"
                sx={{ py: 2 }}
              />
              <Tab
                icon={<SmartToyIcon />}
                label="Chatbot IA"
                sx={{ py: 2 }}
              />
              <Tab
                icon={
                  <Badge color="warning" variant="dot" invisible={!isAppConfigDirty}>
                    <PhoneAndroidIcon />
                  </Badge>
                }
                label="App Móvil"
                sx={{ py: 2 }}
              />
            </Tabs>
          </Paper>

          {/* Pestaña General */}
          <TabPanel value={activeTab} index={0}>
            <SettingsSection 
              title="Configuración General" 
              icon={<BusinessIcon color="primary" />}
              defaultExpanded={true}
            >
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nombre de la AMPA"
                      name="appName"
                      value={settings.appName}
                      onChange={handleSettingsChange}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </SettingsSection>

              <SettingsSection
                title="Configuración de Contacto"
                icon={<ContactPhoneIcon color="primary" />}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email de contacto"
                      name="contactEmail"
                      value={settings.contactEmail}
                      onChange={handleSettingsChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono de contacto"
                      name="contactPhone"
                      value={settings.contactPhone}
                      onChange={handleSettingsChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Dirección del colegio"
                      name="schoolAddress"
                      value={settings.schoolAddress}
                      onChange={handleSettingsChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Mensaje de bienvenida"
                      name="welcomeMessage"
                      value={settings.welcomeMessage}
                      onChange={handleSettingsChange}
                      multiline
                      rows={3}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={3} flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.shopEnabled}
                            onChange={handleSettingsChange}
                            name="shopEnabled"
                            color="primary"
                          />
                        }
                        label="Habilitar tienda"
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.notificationsEnabled}
                            onChange={handleSettingsChange}
                            name="notificationsEnabled"
                            color="primary"
                          />
                        }
                        label="Habilitar notificaciones"
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.servicesEnabled}
                            onChange={handleSettingsChange}
                            name="servicesEnabled"
                            color="primary"
                          />
                        }
                        label="Habilitar servicios"
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </SettingsSection>
          </TabPanel>


          {/* Pestaña Chatbot IA */}
          <TabPanel value={activeTab} index={1}>
            <Stack spacing={3}>
              {/* Información del AMPA */}
              <SettingsSection 
                title="Información del AMPA" 
                icon={<BusinessIcon color="primary" />}
                defaultExpanded={true}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nombre del AMPA"
                      value={chatbotSettings.ampa.nombre}
                      onChange={(e) => handleChatbotSettingsChange('ampa', 'nombre', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Dirección"
                      value={chatbotSettings.ampa.direccion}
                      onChange={(e) => handleChatbotSettingsChange('ampa', 'direccion', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono principal"
                      value={chatbotSettings.ampa.telefono_principal}
                      onChange={(e) => handleChatbotSettingsChange('ampa', 'telefono_principal', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email principal"
                      value={chatbotSettings.ampa.email_principal}
                      onChange={(e) => handleChatbotSettingsChange('ampa', 'email_principal', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Horario de atención"
                      value={chatbotSettings.ampa.horario_atencion}
                      onChange={(e) => handleChatbotSettingsChange('ampa', 'horario_atencion', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Web oficial"
                      value={chatbotSettings.ampa.web_oficial}
                      onChange={(e) => handleChatbotSettingsChange('ampa', 'web_oficial', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Descripción del AMPA"
                      value={chatbotSettings.ampa.descripcion}
                      onChange={(e) => handleChatbotSettingsChange('ampa', 'descripcion', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </SettingsSection>

              {/* Servicios de Comedor */}
              <SettingsSection
                title="Servicios de Comedor"
                icon={<RestaurantIcon color="primary" />}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={chatbotSettings.comedor.activo}
                          onChange={(e) => handleChatbotSettingsChange('comedor', 'activo', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Servicio activo"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Empresa proveedora"
                      value={chatbotSettings.comedor.empresa_proveedora}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'empresa_proveedora', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono de contacto"
                      value={chatbotSettings.comedor.telefono}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'telefono', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={chatbotSettings.comedor.email}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'email', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Web"
                      value={chatbotSettings.comedor.web}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'web', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                      Precios del Comedor
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PriceField
                      label="Niño completo"
                      value={chatbotSettings.comedor.precios.nino_completo}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'precios', e.target.value, 'nino_completo')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PriceField
                      label="Niño medio"
                      value={chatbotSettings.comedor.precios.nino_medio}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'precios', e.target.value, 'nino_medio')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PriceField
                      label="Adulto"
                      value={chatbotSettings.comedor.precios.adulto}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'precios', e.target.value, 'adulto')}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Horarios del comedor"
                      value={chatbotSettings.comedor.horarios}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'horarios', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Información adicional"
                      value={chatbotSettings.comedor.info_adicional}
                      onChange={(e) => handleChatbotSettingsChange('comedor', 'info_adicional', e.target.value)}
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </SettingsSection>

              {/* Servicios de Acogida */}
              <SettingsSection 
                title="Servicios de Acogida" 
                icon={<GroupIcon color="primary" />}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={chatbotSettings.acogida.activo}
                          onChange={(e) => handleChatbotSettingsChange('acogida', 'activo', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Servicio activo"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Empresa proveedora"
                      value={chatbotSettings.acogida.empresa_proveedora}
                      onChange={(e) => handleChatbotSettingsChange('acogida', 'empresa_proveedora', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      value={chatbotSettings.acogida.telefono}
                      onChange={(e) => handleChatbotSettingsChange('acogida', 'telefono', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                      Precios de Acogida
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PriceField
                      label="Acogida matinal"
                      value={chatbotSettings.acogida.precios.matinal}
                      onChange={(e) => handleChatbotSettingsChange('acogida', 'precios', e.target.value, 'matinal')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PriceField
                      label="Acogida tardía"
                      value={chatbotSettings.acogida.precios.tardia}
                      onChange={(e) => handleChatbotSettingsChange('acogida', 'precios', e.target.value, 'tardia')}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <PriceField
                      label="Acogida completa"
                      value={chatbotSettings.acogida.precios.completa}
                      onChange={(e) => handleChatbotSettingsChange('acogida', 'precios', e.target.value, 'completa')}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                      Horarios de Acogida
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Horario matinal"
                      value={chatbotSettings.acogida.horarios.matinal}
                      onChange={(e) => handleChatbotSettingsChange('acogida', 'horarios', e.target.value, 'matinal')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Horario tardía"
                      value={chatbotSettings.acogida.horarios.tardia}
                      onChange={(e) => handleChatbotSettingsChange('acogida', 'horarios', e.target.value, 'tardia')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Información adicional"
                      value={chatbotSettings.acogida.info_adicional}
                      onChange={(e) => handleChatbotSettingsChange('acogida', 'info_adicional', e.target.value)}
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>
              </SettingsSection>

              {/* Actividades Extraescolares */}
              <SettingsSection 
                title="Actividades Extraescolares" 
                icon={<SportsIcon color="primary" />}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={chatbotSettings.extraescolares.activo}
                          onChange={(e) => handleChatbotSettingsChange('extraescolares', 'activo', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Servicio activo"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Información general"
                      value={chatbotSettings.extraescolares.info_general}
                      onChange={(e) => handleChatbotSettingsChange('extraescolares', 'info_general', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Grid>

                  {/* Lista de proveedores existentes */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                      Proveedores de servicios
                    </Typography>
                    
                    {chatbotSettings.extraescolares.proveedores.map((proveedor, index) => (
                      <Card key={index} variant="outlined" sx={{ mb: 2, position: 'relative' }}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" gutterBottom color="primary">
                                {proveedor.nombre}
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                                {proveedor.servicios.map((servicio, idx) => (
                                  <Chip key={idx} label={servicio} size="small" />
                                ))}
                              </Stack>
                              {proveedor.telefono && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PhoneIcon fontSize="small" /> {proveedor.telefono}
                                </Typography>
                              )}
                              {proveedor.email && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <EmailIcon fontSize="small" /> {proveedor.email}
                                </Typography>
                              )}
                              {proveedor.web && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <LinkIcon fontSize="small" /> {proveedor.web}
                                </Typography>
                              )}
                            </Box>
                            <Tooltip title="Eliminar proveedor">
                              <IconButton 
                                onClick={() => handleRemoveProveedor(index)} 
                                color="error"
                                sx={{ mt: -1, mr: -1 }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Grid>

                  {/* Formulario para nuevo proveedor */}
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: 'secondary.light', border: '2px dashed', borderColor: 'primary.main' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          Añadir nuevo proveedor
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Nombre del proveedor"
                              value={chatbotSettings.extraescolares.nuevo_proveedor.nombre}
                              onChange={(e) => handleChatbotSettingsChange('extraescolares', 'nuevo_proveedor', e.target.value, 'nombre')}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Servicios (separados por comas)"
                              value={chatbotSettings.extraescolares.nuevo_proveedor.servicios}
                              onChange={(e) => handleChatbotSettingsChange('extraescolares', 'nuevo_proveedor', e.target.value, 'servicios')}
                              placeholder="ej: fútbol, inglés, robótica"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Teléfono"
                              value={chatbotSettings.extraescolares.nuevo_proveedor.telefono}
                              onChange={(e) => handleChatbotSettingsChange('extraescolares', 'nuevo_proveedor', e.target.value, 'telefono')}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PhoneIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Email"
                              value={chatbotSettings.extraescolares.nuevo_proveedor.email}
                              onChange={(e) => handleChatbotSettingsChange('extraescolares', 'nuevo_proveedor', e.target.value, 'email')}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <EmailIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Web"
                              value={chatbotSettings.extraescolares.nuevo_proveedor.web}
                              onChange={(e) => handleChatbotSettingsChange('extraescolares', 'nuevo_proveedor', e.target.value, 'web')}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <LinkIcon color="primary" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Descripción"
                              value={chatbotSettings.extraescolares.nuevo_proveedor.descripcion}
                              onChange={(e) => handleChatbotSettingsChange('extraescolares', 'nuevo_proveedor', e.target.value, 'descripcion')}
                              multiline
                              rows={2}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={handleAddProveedor}
                              disabled={!chatbotSettings.extraescolares.nuevo_proveedor.nombre.trim()}
                              sx={{ borderRadius: 2 }}
                            >
                              Añadir proveedor
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </SettingsSection>

              {/* Contactos Importantes */}
              <SettingsSection 
                title="Contactos Importantes" 
                icon={<ContactPhoneIcon color="primary" />}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Dirección de la escuela"
                      value={chatbotSettings.contactos.direccion_escuela}
                      onChange={(e) => handleChatbotSettingsChange('contactos', 'direccion_escuela', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                      Secretaría de la escuela
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Teléfono secretaría"
                      value={chatbotSettings.contactos.secretaria_escuela.telefono}
                      onChange={(e) => handleChatbotSettingsChange('contactos', 'secretaria_escuela', e.target.value, 'telefono')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Email secretaría"
                      value={chatbotSettings.contactos.secretaria_escuela.email}
                      onChange={(e) => handleChatbotSettingsChange('contactos', 'secretaria_escuela', e.target.value, 'email')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Horario secretaría"
                      value={chatbotSettings.contactos.secretaria_escuela.horario}
                      onChange={(e) => handleChatbotSettingsChange('contactos', 'secretaria_escuela', e.target.value, 'horario')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                      Conserjería
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono conserjería"
                      value={chatbotSettings.contactos.conserjeria.telefono}
                      onChange={(e) => handleChatbotSettingsChange('contactos', 'conserjeria', e.target.value, 'telefono')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Horario conserjería"
                      value={chatbotSettings.contactos.conserjeria.horario}
                      onChange={(e) => handleChatbotSettingsChange('contactos', 'conserjeria', e.target.value, 'horario')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                      Emergencias
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Teléfono urgencias"
                      value={chatbotSettings.contactos.emergencias.telefono_urgencias}
                      onChange={(e) => handleChatbotSettingsChange('contactos', 'emergencias', e.target.value, 'telefono_urgencias')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="error" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contacto fuera de horario"
                      value={chatbotSettings.contactos.emergencias.contacto_fuera_horario}
                      onChange={(e) => handleChatbotSettingsChange('contactos', 'emergencias', e.target.value, 'contacto_fuera_horario')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="warning" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </SettingsSection>

              {/* Preguntas Frecuentes */}
              <SettingsSection 
                title="Preguntas Frecuentes (FAQ)" 
                icon={<HelpIcon color="primary" />}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Horarios de la escuela"
                      value={chatbotSettings.faq.horarios_escuela}
                      onChange={(e) => handleChatbotSettingsChange('faq', 'horarios_escuela', e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Proceso de inscripción"
                      value={chatbotSettings.faq.proceso_inscripcion}
                      onChange={(e) => handleChatbotSettingsChange('faq', 'proceso_inscripcion', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Becas y ayudas disponibles"
                      value={chatbotSettings.faq.becas_ayudas}
                      onChange={(e) => handleChatbotSettingsChange('faq', 'becas_ayudas', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Actividades del AMPA"
                      value={chatbotSettings.faq.actividades_ampa}
                      onChange={(e) => handleChatbotSettingsChange('faq', 'actividades_ampa', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Cómo participar en el AMPA"
                      value={chatbotSettings.faq.como_participar}
                      onChange={(e) => handleChatbotSettingsChange('faq', 'como_participar', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Fechas de reuniones"
                      value={chatbotSettings.faq.reuniones_fechas}
                      onChange={(e) => handleChatbotSettingsChange('faq', 'reuniones_fechas', e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </SettingsSection>
            </Stack>
          </TabPanel>

          {/* Pestaña App Móvil */}
          <TabPanel value={activeTab} index={2}>
            <Stack spacing={3}>

              {/* Banner TEST visible */}
              {appConfig.stripeTestMode && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{ fontWeight: 700, fontSize: '1rem', borderRadius: 2 }}
                >
                  ⚠️ Stripe en <strong>MODO TEST</strong> — los pagos de la app no son reales
                </Alert>
              )}

              {/* Sección pagos */}
              <SettingsSection
                title="Sistema de Pagos"
                icon={<CreditCardIcon color="primary" />}
                defaultExpanded={true}
              >
                <Grid container spacing={3}>

                  {/* Toggle isPaymentActive */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      p: 2,
                      bgcolor: appConfig.isPaymentActive ? '#E8F5E9' : '#FFEBEE',
                      border: '1px solid',
                      borderColor: appConfig.isPaymentActive ? '#A5D6A7' : '#EF9A9A'
                    }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={appConfig.isPaymentActive}
                            onChange={handleAppConfigChange}
                            name="isPaymentActive"
                            color="primary"
                          />
                        }
                        label={<Typography fontWeight={600}>Cobro de cuota AMPA activo</Typography>}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        {appConfig.isPaymentActive
                          ? 'Los usuarios pueden pagar la cuota (30€) con tarjeta'
                          : 'Los usuarios ven pantalla de acceso limitado sin botón de pago'}
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Toggle stripeTestMode */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{
                      p: 2,
                      bgcolor: appConfig.stripeTestMode ? '#FFF8E1' : '#E8F5E9',
                      border: '1px solid',
                      borderColor: appConfig.stripeTestMode ? '#FFD54F' : '#A5D6A7'
                    }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={appConfig.stripeTestMode}
                              onChange={handleAppConfigChange}
                              name="stripeTestMode"
                              color="warning"
                            />
                          }
                          label={<Typography fontWeight={600}>Modo test de Stripe</Typography>}
                        />
                        <Chip
                          label={appConfig.stripeTestMode ? 'TEST' : 'PRODUCCIÓN'}
                          color={appConfig.stripeTestMode ? 'warning' : 'success'}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {appConfig.stripeTestMode
                          ? 'Usa claves de test — los pagos no se cobran'
                          : 'Usa claves live — los pagos son reales'}
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Cuota AMPA */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cuota anual de socio AMPA"
                      name="ampa-cuote"
                      type="number"
                      value={appConfig['ampa-cuote'] ?? 0}
                      onChange={handleAppConfigChange}
                      helperText="Importe en € que pagan los socios al año"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EuroIcon color="primary" />
                          </InputAdornment>
                        ),
                        inputProps: { min: 0, step: 0.01 }
                      }}
                    />
                  </Grid>

                  {/* URLs descarga app */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="App Store (iOS)"
                      name="appStoreUrl"
                      value={appConfig.appStoreUrl || ''}
                      onChange={handleAppConfigChange}
                      placeholder="https://apps.apple.com/app/id…"
                      helperText="Enlace del portal /portal para iPhone"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Google Play (Android)"
                      name="playStoreUrl"
                      value={appConfig.playStoreUrl || ''}
                      onChange={handleAppConfigChange}
                      placeholder="https://play.google.com/store/apps/details?id=…"
                      helperText="Enlace del portal /portal para Android"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  {/* Webhook URLs */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Webhook URL Producción"
                      name="stripeWebhookUrl"
                      value={appConfig.stripeWebhookUrl}
                      onChange={handleAppConfigChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="success" />
                          </InputAdornment>
                        )
                      }}
                      helperText="URL activa cuando stripeTestMode = OFF"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Webhook URL Test"
                      name="stripeWebhookUrlTest"
                      value={appConfig.stripeWebhookUrlTest}
                      onChange={handleAppConfigChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon color="warning" />
                          </InputAdornment>
                        )
                      }}
                      helperText="URL activa cuando stripeTestMode = ON"
                    />
                  </Grid>
                </Grid>
              </SettingsSection>

              {/* Sección año académico */}
              <SettingsSection
                title="Año Académico"
                icon={<CalendarMonthIcon color="primary" />}
              >
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={4} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Día</InputLabel>
                      <Select
                        value={appConfig.academicYearStartDay}
                        label="Día"
                        name="academicYearStartDay"
                        onChange={handleAppConfigChange}
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                          <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6} sm={8} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Mes de inicio del año académico</InputLabel>
                      <Select
                        value={appConfig.academicYearStartMonth}
                        label="Mes de inicio del año académico"
                        name="academicYearStartMonth"
                        onChange={handleAppConfigChange}
                      >
                        {MESES.map((mes, i) => (
                          <MenuItem key={i + 1} value={i + 1}>{mes}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Días de gracia para renovación"
                      name="renewalDays"
                      value={appConfig.renewalDays ?? 30}
                      onChange={handleAppConfigChange}
                      helperText="Días antes de que la app muestre el popup de renovación"
                      InputProps={{
                        inputProps: { min: 0, step: 1 },
                        endAdornment: (
                          <InputAdornment position="end">días</InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: '#E8F5E9', border: '1px solid #A5D6A7' }}>
                      <Typography variant="h6" color="primary">
                        Año académico actual:{' '}
                        <strong>
                          {getAcademicYear(appConfig.academicYearStartMonth, appConfig.academicYearStartDay)}
                        </strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        El nuevo año empieza el {appConfig.academicYearStartDay} de {MESES[appConfig.academicYearStartMonth - 1]}
                        {appConfig.renewalDays > 0 && ` · El popup de renovación aparece ${appConfig.renewalDays} días antes`}
                      </Typography>
                    </Paper>
                  </Grid>

                  {appConfig.updatedAt && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Última modificación:{' '}
                        {appConfig.updatedAt?.toDate
                          ? appConfig.updatedAt.toDate().toLocaleString('es-ES')
                          : '—'}
                      </Typography>
                    </Grid>
                  )}

                  {isAppConfigDirty && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Hay cambios sin guardar en la configuración de la app
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </SettingsSection>

            </Stack>
          </TabPanel>

          {/* Diálogo confirmación Stripe → producción */}
          <Dialog open={showTestModeConfirm} onClose={() => setShowTestModeConfirm(false)}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon color="warning" /> Activar modo Producción
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Estás cambiando Stripe de <strong>TEST</strong> a <strong>PRODUCCIÓN</strong>.
                A partir de ahora los pagos de los usuarios serán <strong>reales</strong>.
                ¿Confirmas el cambio?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowTestModeConfirm(false)}>Cancelar</Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => { setShowTestModeConfirm(false); doSave(); }}
              >
                Sí, activar producción
              </Button>
            </DialogActions>
          </Dialog>

          {/* Botón de guardar flotante */}
          <Box sx={{ 
            position: 'fixed', 
            bottom: 24, 
            right: 24, 
            zIndex: 1000 
          }}>
            <Tooltip title="Guardar toda la configuración">
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveSettings}
                disabled={saving}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 20px rgba(129, 199, 132, 0.4)',
                  '&:hover': {
                    boxShadow: '0 6px 25px rgba(129, 199, 132, 0.5)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {saving ? 'Guardando...' : 'Guardar configuración'}
              </Button>
            </Tooltip>
          </Box>

          {/* Notificaciones */}
          <Snackbar
            open={success}
            autoHideDuration={4000}
            onClose={() => setSuccess(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              severity="success" 
              variant="filled"
              sx={{ 
                borderRadius: 2,
                fontWeight: 500
              }}
            >
              ✅ Configuración guardada exitosamente
            </Alert>
          </Snackbar>
          
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError('')}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              severity="error" 
              variant="filled"
              sx={{ 
                borderRadius: 2,
                fontWeight: 500
              }}
            >
              ❌ {error}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Settings;